import { useEffect } from "react";

interface SEOHeadProps {
  title: string;
  description?: string;
  canonicalPath?: string;
  jsonLd?: Record<string, any>;
  image?: string;
}

export function SEOHead({ title, description, canonicalPath, jsonLd, image }: SEOHeadProps) {
  useEffect(() => {
    document.title = title;
    
    // Set HTML lang attribute to Romanian
    document.documentElement.setAttribute('lang', 'ro');

    const setMeta = (name: string, content: string) => {
      let el = document.querySelector(`meta[name="${name}"]`) as HTMLMetaElement | null;
      if (!el) {
        el = document.createElement("meta");
        el.setAttribute("name", name);
        document.head.appendChild(el);
      }
      el.setAttribute("content", content);
    };

    if (description) {
      setMeta("description", description);
    }

    // Open Graph
    const setOG = (property: string, content: string) => {
      let el = document.querySelector(`meta[property="${property}"]`) as HTMLMetaElement | null;
      if (!el) {
        el = document.createElement("meta");
        el.setAttribute("property", property);
        document.head.appendChild(el);
      }
      el.setAttribute("content", content);
    };

    setOG("og:title", title);
    setOG("og:locale", "ro_RO");
    setOG("og:type", "website");
    setOG("og:site_name", "MagicDoor");
    if (description) setOG("og:description", description);
    if (canonicalPath) setOG("og:url", `https://magicdoor.ro${canonicalPath}`);
    if (image) {
      setOG("og:image", `https://magicdoor.ro${image}`);
      setOG("og:image:width", "1200");
      setOG("og:image:height", "630");
    }
    
    // Add robots meta tag if specified in jsonLd
    if (jsonLd?.robots) {
      setMeta("robots", jsonLd.robots);
    }

    // Canonical
    if (canonicalPath) {
      const href = `https://magicdoor.ro${canonicalPath}`;
      let link = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
      if (!link) {
        link = document.createElement("link");
        link.setAttribute("rel", "canonical");
        document.head.appendChild(link);
      }
      link.setAttribute("href", href);
      
      // Add hreflang tags for language targeting
      let hreflangRo = document.querySelector('link[hreflang="ro"]') as HTMLLinkElement | null;
      if (!hreflangRo) {
        hreflangRo = document.createElement("link");
        hreflangRo.setAttribute("rel", "alternate");
        hreflangRo.setAttribute("hreflang", "ro");
        document.head.appendChild(hreflangRo);
      }
      hreflangRo.setAttribute("href", href);
      
      let hreflangX = document.querySelector('link[hreflang="x-default"]') as HTMLLinkElement | null;
      if (!hreflangX) {
        hreflangX = document.createElement("link");
        hreflangX.setAttribute("rel", "alternate");
        hreflangX.setAttribute("hreflang", "x-default");
        document.head.appendChild(hreflangX);
      }
      hreflangX.setAttribute("href", href);
    }

    // JSON-LD
    let ldScript: HTMLScriptElement | null = null;
    if (jsonLd) {
      ldScript = document.createElement("script");
      ldScript.type = "application/ld+json";
      ldScript.text = JSON.stringify(jsonLd);
      document.head.appendChild(ldScript);
    }

    return () => {
      if (ldScript) document.head.removeChild(ldScript);
    };
  }, [title, description, canonicalPath, jsonLd]);

  return null;
}

export default SEOHead;
